import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  RotateCcw,
  Search,
  SearchX,
  Users,
  X,
} from "lucide-react";
import {
  addContacts,
  errorMessage,
  listCandidateCompanies,
  listCandidateContacts,
  previewAddContacts,
  type AddContactsRequest,
  type AddPreviewResponse,
  type Campaign,
  type CandidateCompany,
  type CandidateContact,
  type EmailMeta,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import { SelectAllBox } from "@/components/email/SelectAllBox";
import {
  BusinessStatusBadge,
  CompanyEmailBadge,
  ContactStatusBadge,
  Hint,
  TonePill,
} from "@/components/email/EmailBadges";
import { listMembers } from "@/api/projects";
import type { PipelineStatus, ProjectMember, SavedViewRead } from "@/api/types";
import { AddFilterPopover } from "@/components/companies/AddFilterPopover";
import { AddSortButton } from "@/components/companies/AddSortButton";
import { FilterChipsBar } from "@/components/companies/FilterChipsBar";
import { UnifiedFilterRow } from "@/components/companies/UnifiedFilterRow";
import { ContactFilterControls } from "@/components/contacts/ContactFilterControls";
import { ViewsDropdown } from "@/components/views/ViewsDropdown";
import { useCompanyColumns } from "@/hooks/useCompanyColumns";
import { useCompanyFilterChips, useCompanyFilterState } from "@/hooks/useCompanyFilters";
import { useContactFilterState } from "@/hooks/useContactFilters";
import { useDebounce } from "@/hooks/useDebounce";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { useViews } from "@/hooks/useViews";
import { ACTION_LABEL, ELIGIBILITY, LEGACY_ENROLLED, summaryParts } from "@/lib/email";
import { formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;
const ANY = "__any__";
type Mode = "contacts" | "companies";

/**
 * Two steps: pick contacts or whole companies of the project, then see — row
 * by row — who will be added, skipped or moved, before anything is written.
 */
export function AddContactsWizard({
  open,
  onOpenChange,
  slug,
  meta,
  campaigns,
  defaultCampaignId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The project: every list, campaign and contact here is its own. */
  slug: string;
  meta: EmailMeta;
  /** The project's campaigns a contact can be added to. */
  campaigns: Campaign[];
  defaultCampaignId: number | null;
  onDone: () => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [mode, setMode] = useState<Mode>("contacts");
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [pickedContacts, setPickedContacts] = useState<Map<number, CandidateContact>>(new Map());
  const [pickedCompanies, setPickedCompanies] = useState<Map<number, CandidateCompany>>(new Map());

  useEffect(() => {
    if (!open) return;
    const initial = campaigns.find((c) => c.id === defaultCampaignId) ?? campaigns[0] ?? null;
    setStep(1);
    setMode("contacts");
    setCampaignId(initial?.id ?? null);
    setPickedContacts(new Map());
    setPickedCompanies(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [open]);

  const selectedCount = pickedContacts.size + pickedCompanies.size;
  const companyContacts = [...pickedCompanies.values()].reduce((sum, c) => sum + c.eligible_contacts_count, 0);
  const destination = campaigns.find((c) => c.id === campaignId) ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(88vh,880px)] max-w-6xl flex-col gap-0 overflow-hidden p-0">
        {/* pr-14 keeps the stepper clear of the dialog's close button. */}
        <header className="flex items-center justify-between gap-6 border-b border-slate-100 py-4 pl-6 pr-14">
          <div className="min-w-0">
            <DialogTitle className="text-lg">Add leads</DialogTitle>
            <DialogDescription className="mt-0.5 truncate text-sm text-slate-500">
              Pick contacts or whole companies, then review who is added. Nothing is sent until the campaign is active.
            </DialogDescription>
          </div>
          <div className="shrink-0">
            <Stepper step={step} />
          </div>
        </header>

        {step === 1 ? (
          <SelectStep
            // A project's filters and views are its own: a new project starts clean.
            key={slug}
            slug={slug}
            meta={meta}
            mode={mode}
            onMode={setMode}
            pickedContacts={pickedContacts}
            setPickedContacts={setPickedContacts}
            pickedCompanies={pickedCompanies}
            setPickedCompanies={setPickedCompanies}
          />
        ) : (
          <ReviewStep
            slug={slug}
            meta={meta}
            campaigns={campaigns}
            campaignId={campaignId}
            onCampaign={setCampaignId}
            pickedContacts={pickedContacts}
            pickedCompanies={pickedCompanies}
            onDone={() => {
              onOpenChange(false);
              onDone();
            }}
            onBack={() => setStep(1)}
          />
        )}

        {step === 1 && (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-3.5">
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm text-slate-600">
                {selectedCount ? (
                  <>
                    <span className="font-semibold text-slate-900">{formatNumber(pickedContacts.size)}</span>{" "}
                    {pickedContacts.size === 1 ? "contact" : "contacts"}
                    {pickedCompanies.size > 0 && (
                      <>
                        {" "}and <span className="font-semibold text-slate-900">{formatNumber(pickedCompanies.size)}</span>{" "}
                        {pickedCompanies.size === 1 ? "company" : "companies"}
                        <span className="text-slate-400">
                          {" "}({formatNumber(companyContacts)} {companyContacts === 1 ? "contact" : "contacts"} with an email)
                        </span>
                      </>
                    )}{" "}
                    selected
                  </>
                ) : (
                  <span className="text-slate-400">Select contacts or companies</span>
                )}
                {selectedCount > 0 && (
                  <button
                    type="button"
                    className="ml-3 text-xs font-medium text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
                    onClick={() => {
                      setPickedContacts(new Map());
                      setPickedCompanies(new Map());
                    }}
                  >
                    Clear
                  </button>
                )}
              </p>
              <p className="truncate text-xs text-slate-400">
                {destination
                  ? `Into “${destination.name}”${campaigns.length > 1 ? " · you can change it at review" : ""}`
                  : "Create a campaign first: contacts are added to one."}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!selectedCount || !campaigns.length} onClick={() => setStep(2)}>
                Review
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </footer>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ step }: { step: 1 | 2 }) {
  const items = [
    { n: 1, label: "Select" },
    { n: 2, label: "Review" },
  ];
  return (
    <ol className="flex items-center gap-2 text-xs font-medium">
      {items.map((item, index) => {
        const done = step > item.n;
        const current = step === item.n;
        return (
          <li key={item.n} className="flex items-center gap-2">
            {index > 0 && <span className="h-px w-6 bg-slate-200" />}
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-[11px]",
                current ? "bg-indigo-600 text-white shadow-cta" : done ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400",
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : item.n}
            </span>
            <span className={current ? "text-slate-900" : "text-slate-400"}>{item.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ── step 1 ───────────────────────────────────────────────────────────────

function SelectStep({
  slug,
  meta,
  mode,
  onMode,
  pickedContacts,
  setPickedContacts,
  pickedCompanies,
  setPickedCompanies,
}: {
  slug: string;
  meta: EmailMeta;
  mode: Mode;
  onMode: (mode: Mode) => void;
  pickedContacts: Map<number, CandidateContact>;
  setPickedContacts: (next: Map<number, CandidateContact>) => void;
  pickedCompanies: Map<number, CandidateCompany>;
  setPickedCompanies: (next: Map<number, CandidateCompany>) => void;
}) {
  const perms = usePermissions();
  const scoringEnabled = useFeature("scoring");
  const statuses = meta.business_statuses;

  // Companies: the Companies page's filters, chips and saved views.
  const companyFilters = useCompanyFilterState("prospects");
  const availableColumns = useCompanyColumns(mode === "companies" ? slug : null, {
    canViewScore: perms.canViewScore && scoringEnabled,
    canViewTags: perms.canViewTags,
  });
  const [members, setMembers] = useState<ProjectMember[]>([]);
  useEffect(() => {
    if (!slug || mode !== "companies") return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug, mode]);
  const pipelineStatuses = useMemo<PipelineStatus[]>(
    () => statuses.map((s, index) => ({ key: s.key, label: s.label, color: s.color ?? undefined, order: index + 1 })),
    [statuses],
  );
  const chips = useCompanyFilterChips(companyFilters, { availableColumns, pipelineStatuses, members });
  const companyViews = useViews(mode === "companies" ? slug : null, companyFilters.entity);

  // Contacts: the Contacts page's filters and saved views.
  const contactFilters = useContactFilterState(mode === "contacts" ? slug : null);
  const contactViews = useViews(mode === "contacts" ? slug : null, "contacts");

  const viewsApi = mode === "companies" ? companyViews : contactViews;
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const activeView = viewsApi.views.find((v) => v.id === activeViewId) ?? null;
  useEffect(() => setActiveViewId(null), [mode, companyFilters.entity]);

  // The wizard's own filters, about email.
  const [businessStatus, setBusinessStatus] = useState(ANY);
  const [role, setRole] = useState("");
  const debouncedRole = useDebounce(role, 300);
  // Contacts and companies with an email by default: the others cannot be emailed.
  const [hasEmail, setHasEmail] = useState("yes");
  const [withEmail, setWithEmail] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState(ANY);
  const [onlyReady, setOnlyReady] = useState(false);

  const [page, setPage] = useState(1);
  const [contacts, setContacts] = useState<CandidateContact[]>([]);
  const [companies, setCompanies] = useState<CandidateCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { listParams: contactParams, debouncedSearch: contactSearch } = contactFilters;
  const { listFilters: companyParams } = companyFilters;
  useEffect(
    () => setPage(1),
    [mode, contactParams, contactSearch, companyParams, businessStatus, debouncedRole, hasEmail, campaignFilter, onlyReady, withEmail],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === "contacts") {
        const res = await listCandidateContacts(slug, {
          ...contactParams,
          search: contactSearch.trim(),
          business_status: businessStatus === ANY ? [] : [businessStatus],
          role: debouncedRole.trim(),
          has_email: hasEmail === ANY ? null : hasEmail === "yes",
          campaign_id: campaignFilter === ANY ? null : campaignFilter === "none" ? "none" : Number(campaignFilter),
          page,
          page_size: PAGE_SIZE,
        });
        setContacts(res.items);
        setTotal(res.total);
      } else {
        const res = await listCandidateCompanies(slug, {
          list: companyParams,
          only_with_ready: onlyReady,
          with_email: withEmail,
          page,
          page_size: PAGE_SIZE,
        });
        setCompanies(res.items);
        setTotal(res.total);
      }
    } catch (err) {
      setError(errorMessage(err, "The list could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [slug, mode, contactParams, contactSearch, companyParams, businessStatus, debouncedRole, hasEmail, campaignFilter, onlyReady, withEmail, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = mode === "contacts" ? contacts : companies;
  const picked = mode === "contacts" ? pickedContacts : pickedCompanies;
  // Rows that can bring someone: the others are shown, never picked.
  const addable = mode === "contacts" ? contacts.filter((r) => !contactBlock(r)) : companies.filter((r) => !companyBlock(r));
  const pageAll = addable.length > 0 && addable.every((r) => picked.has(r.id));
  const pageSome = addable.some((r) => picked.has(r.id));
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(page * PAGE_SIZE, total);
  const noun = mode === "contacts" ? (total === 1 ? "contact" : "contacts") : total === 1 ? "company" : "companies";

  /** Pick (or drop) rows of this page; rows that cannot be added are left out. */
  function setRows(list: (CandidateContact | CandidateCompany)[], on: boolean) {
    if (mode === "contacts") {
      const next = new Map(pickedContacts);
      for (const row of list as CandidateContact[]) {
        if (contactBlock(row)) continue;
        if (on) next.set(row.id, row);
        else next.delete(row.id);
      }
      setPickedContacts(next);
    } else {
      const next = new Map(pickedCompanies);
      for (const row of list as CandidateCompany[]) {
        if (companyBlock(row)) continue;
        if (on) next.set(row.id, row);
        else next.delete(row.id);
      }
      setPickedCompanies(next);
    }
  }

  // Shift-click picks every row between the last clicked one and this one.
  const lastClicked = useRef<number | null>(null);
  useEffect(() => {
    lastClicked.current = null;
  }, [mode, page]);
  function clickRow(index: number, shift: boolean) {
    const list: (CandidateContact | CandidateCompany)[] = mode === "contacts" ? contacts : companies;
    const on = !picked.has(list[index].id);
    const anchor = shift && lastClicked.current !== null ? lastClicked.current : index;
    setRows(list.slice(Math.min(anchor, index), Math.max(anchor, index) + 1), on);
    lastClicked.current = index;
  }

  function pickView(view: SavedViewRead) {
    setActiveViewId(view.id);
    if (mode === "companies") companyFilters.applyView(view);
    else contactFilters.applyView(view);
  }

  const contactsFiltered =
    contactFilters.search.trim() !== "" ||
    contactFilters.companyFilter !== null ||
    contactFilters.dmFilter !== "all" ||
    contactFilters.enrichmentFilter !== null ||
    businessStatus !== ANY ||
    role.trim() !== "" ||
    hasEmail !== "yes" ||
    campaignFilter !== ANY ||
    activeViewId !== null;
  const companiesFiltered = companyFilters.anyFilterActive || !withEmail || onlyReady || activeViewId !== null;
  function resetFilters() {
    setActiveViewId(null);
    if (mode === "contacts") {
      contactFilters.setSearch("");
      contactFilters.pickCompany(null);
      contactFilters.setDmFilter("all");
      contactFilters.setEnrichmentFilter(null);
      setBusinessStatus(ANY);
      setRole("");
      setHasEmail("yes");
      setCampaignFilter(ANY);
    } else {
      companyFilters.setTab("prospects");
      companyFilters.resetFilters();
      setWithEmail(true);
      setOnlyReady(false);
    }
  }
  const filtered = mode === "contacts" ? contactsFiltered : companiesFiltered;

  const control = "h-8 rounded-lg border-slate-200 bg-white text-xs";
  const columns: { label: string; hint?: string; className?: string }[] =
    mode === "contacts"
      ? [
          { label: "Contact" },
          { label: "Company" },
          { label: "Email" },
          { label: "Business status" },
          { label: "Email activity", hint: "Its status in its current or last campaign of the Email module." },
        ]
      : [
          { label: "Company" },
          { label: "Business status" },
          { label: "Score", className: "text-right" },
          { label: "Email" },
          {
            label: "Eligible",
            hint: "Contacts with an email that can be added (not unsubscribed, not bounced), out of all its contacts.",
            className: "text-right",
          },
          { label: "Ready to add", hint: "Eligible contacts in no running campaign: added as they are.", className: "text-right" },
        ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2.5 border-b border-slate-100 bg-slate-50/40 px-6 py-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div role="radiogroup" aria-label="Select" className="inline-flex shrink-0 rounded-lg bg-slate-100/80 p-0.5">
            {(
              [
                { value: "contacts", label: "Contacts", icon: Users },
                { value: "companies", label: "Companies", icon: Building2 },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={mode === option.value}
                onClick={() => onMode(option.value)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  mode === option.value ? "bg-white text-slate-900 shadow-sm-tile" : "text-slate-500 hover:text-slate-800",
                )}
              >
                <option.icon className="h-3.5 w-3.5" />
                {option.label}
                {(option.value === "contacts" ? pickedContacts.size : pickedCompanies.size) > 0 && (
                  <span className="rounded-full bg-indigo-600 px-1.5 text-[10px] font-semibold text-white">
                    {formatNumber(option.value === "contacts" ? pickedContacts.size : pickedCompanies.size)}
                  </span>
                )}
              </button>
            ))}
          </div>
          {mode === "contacts" && (
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                value={contactFilters.search}
                onChange={(e) => contactFilters.setSearch(e.target.value)}
                placeholder="Search a name, an email or a company…"
                className="h-9 rounded-lg border-slate-200 bg-white pl-9"
              />
            </div>
          )}
          <div className={cn("flex items-center gap-2", mode === "companies" && "ml-auto")}>
            {/* The views saved on the Companies (by tab) and Contacts pages. */}
            <ViewsDropdown
              views={viewsApi.views}
              loading={viewsApi.isLoading}
              activeView={activeView}
              canCreate={false}
              canShare={perms.canShareViews}
              onPickView={pickView}
              onRename={async (view, name) => {
                await viewsApi.rename(view.id, name);
              }}
              onToggleShared={async (view) => {
                await viewsApi.setShared(view.id, !view.is_shared);
              }}
              onToggleDefault={async (view) => {
                await viewsApi.toggleDefault(view.id, view.is_default);
              }}
              onDelete={async (view) => {
                await viewsApi.remove(view.id);
              }}
              onCreateNewClick={() => undefined}
            />
          </div>
        </div>

        {mode === "contacts" ? (
          <div className="flex flex-wrap items-center gap-2">
            <ContactFilterControls slug={slug ?? ""} state={contactFilters} compact />
            <Select value={businessStatus} onValueChange={setBusinessStatus}>
              <SelectTrigger className={cn(control, "w-[150px]")} aria-label="Business status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any business status</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Title: CEO, founder…"
              className={cn(control, "w-[120px]")}
            />
            <Select value={hasEmail} onValueChange={setHasEmail}>
              <SelectTrigger className={cn(control, "w-[115px]", hasEmail === "yes" && "border-indigo-200 bg-indigo-50/60 text-indigo-700")} aria-label="Email">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">With email</SelectItem>
                <SelectItem value="no">Without email</SelectItem>
                <SelectItem value={ANY}>Email: any</SelectItem>
              </SelectContent>
            </Select>
            <Select value={campaignFilter} onValueChange={setCampaignFilter}>
              <SelectTrigger className={cn(control, "w-[150px]")} aria-label="Current campaign">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any campaign status</SelectItem>
                <SelectItem value="none">In no campaign</SelectItem>
              </SelectContent>
            </Select>
            {contactsFiltered && (
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-500" onClick={resetFilters}>
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </Button>
            )}
          </div>
        ) : (
          <>
            <UnifiedFilterRow
              slug={slug ?? ""}
              search={companyFilters.search}
              onSearchChange={companyFilters.setSearch}
              onClearFilters={resetFilters}
              showClearFilters={companiesFiltered}
              addFilterSlot={
                chips.activeFilters.length === 0 ? (
                  <AddFilterPopover
                    fields={chips.pickerFieldList}
                    slug={slug ?? ""}
                    members={members}
                    pipelineStatuses={pipelineStatuses}
                    onAdd={companyFilters.addFilter}
                    variant="toolbar"
                  />
                ) : null
              }
              addSortSlot={
                chips.activeFilters.length === 0 ? (
                  <AddSortButton sorts={companyFilters.sorts} onSortsChange={companyFilters.setSorts} variant="toolbar" />
                ) : null
              }
              columnsSlot={
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-medium text-slate-600">
                    <Switch checked={withEmail} onCheckedChange={setWithEmail} />
                    With an email
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-medium text-slate-600">
                    <Switch checked={onlyReady} onCheckedChange={setOnlyReady} />
                    Ready to add only
                  </label>
                </div>
              }
            />
            {chips.activeFilters.length > 0 && (
              <FilterChipsBar
                activeFilters={chips.activeFilters}
                resolveField={chips.resolveField}
                resolveCondition={chips.resolveCondition}
                pickerFields={chips.pickerFieldList}
                slug={slug ?? ""}
                members={members}
                pipelineStatuses={pipelineStatuses}
                sorts={companyFilters.sorts}
                onSortsChange={companyFilters.setSorts}
                onEditFilter={companyFilters.editFilter}
                onRemoveFilter={companyFilters.removeFilter}
                onAddFilter={companyFilters.addFilter}
              />
            )}
          </>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-auto">
        {error ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-rose-600">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : loading && !rows.length ? (
          <div className="space-y-2 p-6">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-11 w-full rounded-lg" />
            ))}
          </div>
        ) : !rows.length ? (
          <div className="flex flex-col items-center px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <SearchX className="h-5 w-5" />
            </span>
            <p className="mt-3 text-sm font-medium text-slate-700">
              {mode === "contacts" ? "No contact matches these filters" : "No company matches these filters"}
            </p>
            {filtered && (
              <Button variant="outline" size="sm" className="mt-4" onClick={resetFilters}>
                <RotateCcw className="h-3.5 w-3.5" />
                Reset the filters
              </Button>
            )}
          </div>
        ) : (
          <table className={cn("w-full text-sm", mode === "contacts" ? "min-w-[880px]" : "min-w-[760px]")}>
            <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur">
              <tr className="border-b border-slate-100 text-left">
                <th className="w-12 py-2.5 pl-6 pr-2">
                  <SelectAllBox label="Select this page" all={pageAll} some={pageSome} onChange={(on) => setRows(rows, on)} />
                </th>
                {columns.map((column) => (
                  <th
                    key={column.label}
                    className={cn("px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400", column.className)}
                  >
                    {column.hint ? (
                      <Hint content={column.hint}>
                        <span className="inline-flex cursor-help items-center gap-1">
                          {column.label}
                          <Info className="h-3 w-3 text-slate-300" />
                        </span>
                      </Hint>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={cn("transition-opacity", loading && "opacity-60")}>
              {mode === "contacts"
                ? contacts.map((row, index) => {
                    const block = contactBlock(row);
                    const on = pickedContacts.has(row.id);
                    return (
                      <tr
                        key={row.id}
                        onClick={(e) => !block && clickRow(index, e.shiftKey)}
                        // Shift-click picks a range: no text selection with it.
                        onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                        className={cn(
                          "border-b border-slate-50 transition-colors",
                          block ? "cursor-default text-slate-400" : "cursor-pointer hover:bg-indigo-50/40",
                          on && "bg-indigo-50/60",
                        )}
                      >
                        <td className="py-2.5 pl-6 pr-2" onClick={(e) => e.stopPropagation()}>
                          {block ? (
                            <Hint content={block}>
                              <span className="inline-flex">
                                <Checkbox aria-label={`${row.full_name} cannot be added`} disabled checked={false} className="border-slate-200" />
                              </span>
                            </Hint>
                          ) : (
                            <Checkbox
                              aria-label={`Select ${row.full_name}`}
                              checked={on}
                              onCheckedChange={(c) => setRows([row], !!c)}
                              className="border-slate-300 data-[state=checked]:border-indigo-600 data-[state=checked]:bg-indigo-600"
                            />
                          )}
                        </td>
                        <td className="max-w-[230px] px-3 py-2.5">
                          <p className={cn("flex items-center gap-1.5 truncate font-medium", block ? "text-slate-500" : "text-slate-900")}>
                            <span className="truncate">{row.full_name}</span>
                            {row.is_decision_maker && (
                              <span className="shrink-0 rounded bg-indigo-50 px-1 text-[10px] font-semibold text-indigo-600">DM</span>
                            )}
                          </p>
                          <p className="truncate text-xs text-slate-500">{row.title ?? "—"}</p>
                        </td>
                        <td className="max-w-[190px] px-3 py-2.5">
                          <span className="block truncate text-slate-700" title={row.company_name ?? undefined}>
                            {row.company_name ?? "—"}
                          </span>
                        </td>
                        <td className="max-w-[220px] px-3 py-2.5">
                          {isHidden(row, "email") ? (
                            <HiddenDetail label="Email" className="text-xs" />
                          ) : row.email ? (
                            <span className="block truncate text-slate-600" title={row.email}>
                              {row.email}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">No email</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <BusinessStatusBadge status={row.business_status} label={row.business_status_label} statuses={meta.business_statuses} />
                        </td>
                        <td className="max-w-[200px] px-3 py-2.5">
                          {row.legacy_enrolled ? (
                            <Hint content={ELIGIBILITY.LEGACY_ENROLLED.hint}>
                              <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                {LEGACY_ENROLLED}
                              </span>
                            </Hint>
                          ) : row.email_status ? (
                            <div className="min-w-0 space-y-0.5">
                              <ContactStatusBadge status={row.email_status} />
                              {row.current_campaign_name && (
                                <p className="truncate text-[11px] text-slate-500" title={row.current_campaign_name}>
                                  {row.current_campaign_name}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Not in a campaign</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                : companies.map((row, index) => {
                    const block = companyBlock(row);
                    const on = pickedCompanies.has(row.id);
                    return (
                      <tr
                        key={row.id}
                        onClick={(e) => !block && clickRow(index, e.shiftKey)}
                        // Shift-click picks a range: no text selection with it.
                        onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                        className={cn(
                          "border-b border-slate-50 transition-colors",
                          block ? "cursor-default" : "cursor-pointer hover:bg-indigo-50/40",
                          on && "bg-indigo-50/60",
                        )}
                      >
                        <td className="py-2.5 pl-6 pr-2" onClick={(e) => e.stopPropagation()}>
                          {block ? (
                            <Hint content={block}>
                              <span className="inline-flex">
                                <Checkbox aria-label={`${row.name} cannot be added`} disabled checked={false} className="border-slate-200" />
                              </span>
                            </Hint>
                          ) : (
                            <Checkbox
                              aria-label={`Select ${row.name}`}
                              checked={on}
                              onCheckedChange={(c) => setRows([row], !!c)}
                              className="border-slate-300 data-[state=checked]:border-indigo-600 data-[state=checked]:bg-indigo-600"
                            />
                          )}
                        </td>
                        <td className="max-w-[260px] px-3 py-2.5">
                          <p className={cn("truncate font-medium", block ? "text-slate-500" : "text-slate-900")} title={row.name}>
                            {row.name}
                          </p>
                          <p className="truncate text-xs text-slate-500">{row.domain ?? "—"}</p>
                        </td>
                        <td className="px-3 py-2.5">
                          <BusinessStatusBadge status={row.business_status} label={row.business_status_label} statuses={meta.business_statuses} />
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{row.score ?? "—"}</td>
                        <td className="px-3 py-2.5">
                          {row.legacy_enrolled ? (
                            <Hint content={ELIGIBILITY.LEGACY_ENROLLED.hint}>
                              <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                Legacy workflow
                              </span>
                            </Hint>
                          ) : (
                            <CompanyEmailBadge status={row.email_status} />
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                          {row.eligible_contacts_count}
                          <span className="text-slate-400"> / {row.contacts_count}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span
                            className={cn(
                              "inline-flex min-w-[2rem] justify-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                              row.email_ready_contacts_count ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400",
                            )}
                          >
                            {row.email_ready_contacts_count}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-6 py-2 text-xs text-slate-500">
        <span>
          {total ? (
            <>
              <span className="font-medium tabular-nums text-slate-700">
                {formatNumber(first)}–{formatNumber(last)}
              </span>{" "}
              of <span className="font-medium tabular-nums text-slate-700">{formatNumber(total)}</span> {noun}
            </>
          ) : (
            `0 ${noun}`
          )}
          <span className="text-slate-400">
            {" · "}
            {mode === "companies"
              ? "a company brings all its eligible contacts"
              : "shift-click to pick a range"}
          </span>
        </span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="px-1 tabular-nums">
            Page {formatNumber(page)} of {formatNumber(totalPages)}
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Why a contact cannot be added at all (the review would skip it); null when it can. */
function contactBlock(row: CandidateContact): string | null {
  if (row.legacy_enrolled) return ELIGIBILITY.LEGACY_ENROLLED.hint ?? LEGACY_ENROLLED;
  if (!row.has_email) return ELIGIBILITY.NO_EMAIL.hint ?? "No email.";
  return null;
}

/** Why a company would bring nobody; null when it brings someone. */
function companyBlock(row: CandidateCompany): string | null {
  if (row.legacy_enrolled) return ELIGIBILITY.LEGACY_ENROLLED.hint ?? LEGACY_ENROLLED;
  if (!row.eligible_contacts_count) return "None of its contacts has an email that can be used.";
  return null;
}

// ── step 2 ───────────────────────────────────────────────────────────────

function ReviewStep({
  slug,
  meta,
  campaigns,
  campaignId,
  onCampaign,
  pickedContacts,
  pickedCompanies,
  onDone,
  onBack,
}: {
  slug: string;
  meta: EmailMeta;
  campaigns: Campaign[];
  campaignId: number | null;
  onCampaign: (id: number) => void;
  pickedContacts: Map<number, CandidateContact>;
  pickedCompanies: Map<number, CandidateCompany>;
  onDone: () => void;
  onBack: () => void;
}) {
  const [moveExisting, setMoveExisting] = useState(false);
  const [includeUnsubscribed, setIncludeUnsubscribed] = useState(false);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [show, setShow] = useState<"ALL" | "ADD" | "MOVE" | "SKIP">("ALL");
  const [preview, setPreview] = useState<AddPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const campaign = campaigns.find((c) => c.id === campaignId) ?? null;
  // Moving a contact from another campaign is enrolling it here.
  const canMove = meta.project.can_enroll;
  const request: AddContactsRequest = useMemo(
    () => ({
      contact_ids: [...pickedContacts.keys()],
      company_ids: [...pickedCompanies.keys()],
      move_existing: moveExisting,
      include_unsubscribed: includeUnsubscribed,
      exclude_contact_ids: [...excluded],
    }),
    [pickedContacts, pickedCompanies, moveExisting, includeUnsubscribed, excluded],
  );

  useEffect(() => {
    if (!campaign) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    previewAddContacts(slug, campaign.id, request)
      .then((res) => !cancelled && setPreview(res))
      .catch((err) => !cancelled && setError(errorMessage(err, "The selection could not be checked.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, campaign, request]);

  async function submit() {
    if (!campaign || !preview) return;
    setSaving(true);
    try {
      const res = await addContacts(slug, campaign.id, request);
      const parts = [`${formatNumber(res.added)} added`];
      if (res.moved) parts.push(`${formatNumber(res.moved)} moved`);
      if (res.skipped) parts.push(`${formatNumber(res.skipped)} skipped`);
      toast.success(`“${campaign.name}”: ${parts.join(", ")}`);
      onDone();
    } catch (err) {
      setError(errorMessage(err, "The contacts could not be added."));
    } finally {
      setSaving(false);
    }
  }

  const summary = preview?.summary;
  const willWrite = (summary?.will_add ?? 0) + (summary?.will_move ?? 0);
  const shown = (preview?.rows ?? []).filter((row) => show === "ALL" || row.action === show);
  const tabs = [
    { key: "ALL" as const, label: "All", count: preview?.rows.length ?? 0 },
    { key: "ADD" as const, label: "Added", count: summary?.will_add ?? 0 },
    { key: "MOVE" as const, label: "Moved", count: summary?.will_move ?? 0 },
    { key: "SKIP" as const, label: "Skipped", count: summary?.will_skip ?? 0 },
  ].filter((tab) => tab.key === "ALL" || tab.count > 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid gap-4 border-b border-slate-100 px-6 py-4 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="space-y-1.5">
          <p className="type-zonehead">Destination campaign</p>
          {campaigns.length === 1 ? (
            <p className="flex h-10 items-center text-sm font-medium text-slate-800">{campaigns[0].name}</p>
          ) : (
            <Select value={campaignId === null ? undefined : String(campaignId)} onValueChange={(v) => onCampaign(Number(v))}>
              <SelectTrigger className="h-10 max-w-md rounded-lg border-slate-200" aria-label="Destination campaign">
                <SelectValue placeholder="Choose a campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex flex-col justify-end gap-2">
          <label className={cn("flex items-center gap-2 text-sm text-slate-600", !canMove && "opacity-50")}>
            <Switch checked={moveExisting} disabled={!canMove} onCheckedChange={setMoveExisting} />
            Move contacts already in another campaign
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Switch checked={includeUnsubscribed} onCheckedChange={setIncludeUnsubscribed} />
            Include unsubscribed contacts
          </label>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 border-b border-slate-100 bg-slate-50/50 px-6 py-4 sm:grid-cols-4">
          <SummaryTile label="Selected" value={summary.selected} />
          <SummaryTile label="Will be added" value={summary.will_add} tone="emerald" />
          <SummaryTile label="Will be moved" value={summary.will_move} tone={summary.will_move ? "amber" : undefined} />
          <SummaryTile label="Skipped" value={summary.will_skip} tone="slate" />
          {summaryParts(summary).length > 0 && (
            <p className="col-span-full text-xs text-slate-500">{summaryParts(summary).join(" · ")}</p>
          )}
        </div>
      )}

      {preview && tabs.length > 2 && (
        <div className="flex items-center gap-1 border-b border-slate-100 px-6 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setShow(tab.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                show === tab.key ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
              )}
            >
              {tab.label}
              <span className={cn("tabular-nums", show === tab.key ? "text-white/70" : "text-slate-400")}>
                {formatNumber(tab.count)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <p className="p-8 text-center text-sm text-rose-600">{error}</p>
        ) : !preview || (loading && !preview.rows.length) ? (
          <div className="space-y-2 p-6">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <table className="w-full min-w-[920px] text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-100 text-left">
                {["Contact", "Company", "Email", "Current campaign", "Eligibility", "Action", ""].map((label, i) => (
                  <th key={label || i} className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400 first:pl-6">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={cn(loading && "opacity-60")}>
              {shown.map((row) => {
                const tone = ELIGIBILITY[row.eligibility];
                return (
                  <tr key={row.contact_id} className="border-b border-slate-50">
                    <td className="max-w-[220px] py-2 pl-6 pr-3">
                      <p className="truncate font-medium text-slate-900">{row.full_name}</p>
                      <p className="truncate text-xs text-slate-500">{row.title ?? "—"}</p>
                    </td>
                    <td className="max-w-[180px] truncate px-3 py-2 text-slate-700">{row.company_name ?? "—"}</td>
                    <td className="max-w-[200px] px-3 py-2">
                      {isHidden(row, "email") ? (
                        <HiddenDetail label="Email" className="text-xs" />
                      ) : (
                        <span className="block truncate text-slate-600">{row.email ?? "—"}</span>
                      )}
                    </td>
                    <td className="max-w-[170px] truncate px-3 py-2 text-xs text-slate-600">{row.current_campaign_name ?? "—"}</td>
                    <td className="px-3 py-2">
                      <TonePill tone={tone} />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          "text-xs font-semibold",
                          row.action === "ADD" ? "text-emerald-700" : row.action === "MOVE" ? "text-amber-700" : "text-slate-400",
                        )}
                      >
                        {ACTION_LABEL[row.action]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Hint content="Take this contact out of the selection">
                        <button
                          type="button"
                          aria-label={`Remove ${row.full_name} from the selection`}
                          onClick={() => setExcluded((prev) => new Set(prev).add(row.contact_id))}
                          className="rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </Hint>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-3.5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          {excluded.size > 0 && (
            <button type="button" className="text-xs font-medium text-slate-500 hover:text-slate-700" onClick={() => setExcluded(new Set())}>
              Restore {excluded.size} removed {excluded.size === 1 ? "row" : "rows"}
            </button>
          )}
        </div>
        <Button variant="primary" disabled={!campaign || !willWrite || saving || loading} onClick={() => void submit()}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {summary
            ? summary.will_move
              ? `Add ${formatNumber(summary.will_add)} · move ${formatNumber(summary.will_move)}`
              : `Add ${formatNumber(summary.will_add)} ${summary.will_add === 1 ? "lead" : "leads"}`
            : "Add"}
        </Button>
      </footer>
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone?: "emerald" | "amber" | "slate" }) {
  return (
    <div className="rounded-xl bg-white px-4 py-3 shadow-sm-tile">
      <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">{label}</p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "emerald" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "slate" ? "text-slate-500" : "text-slate-900",
        )}
      >
        {formatNumber(value)}
      </p>
    </div>
  );
}
